import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useTheme } from 'styled-components';

import { getHistoricSystemData } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { useOnDateChange } from '../../hooks';
import type { ChartDataPoint, SystemEnergyHistoryDataItem } from '../../types/charts';
import BatteryStateOfCharge from '../../ui/Dashboard/EnergyFlow/BatteryStateOfCharge';
import SystemEnergyHistoryChart from '../../ui/Dashboard/EnergyFlow/SystemEnergyHistoryChart';
import InfoModal from '../../ui/Modal/InfoModal';
import SmartTariffWarning from '../../ui/SmartTariffWarning';
import WarningPanel from '../../ui/WarningPanel';
import { calculateYDomain, processSocChartData, processSystemEnergyHistoryChartData } from '../../utils/charts';
import getDateFromTimestamp from '../../utils/getDateFromTimestamp';

const EnergyFlowContainer = () => {
  const theme = useTheme();
  const history = useHistory();
  const [fetchingAnalyticsData, setFetchingAnalyticsData] = useState(false);
  const [socChartData, setSocChartData] = useState<ChartDataPoint[]>([]);
  const [systemEnergyHistoryChartData, setSystemEnergyHistoryChartData] = useState<SystemEnergyHistoryDataItem[]>([]);
  const [yDomain, setYDomain] = useState<[number, number]>([0, 0]);
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const { analyticsStartDate, setDatePickerWarning, userSub, userDetails, selectedDate, setSelectedDate } =
    useContext(AppContext);

  const isLoadingUserDetails = !userDetails;

  // Date range constraints from userDetails for arrow navigation
  const minDate = userDetails?.plant_data_range?.start_datetime
    ? getDateFromTimestamp(userDetails.plant_data_range.start_datetime)
    : null;
  const maxDate = userDetails?.plant_data_range?.end_datetime
    ? getDateFromTimestamp(userDetails.plant_data_range.end_datetime)
    : moment().subtract(1, 'days').toDate();

  const isAtMinDate = minDate ? moment(selectedDate).isSameOrBefore(moment(minDate), 'day') : false;
  const isAtMaxDate = moment(selectedDate).isSameOrAfter(moment(maxDate), 'day');

  const handlePreviousDay = useCallback(() => {
    if (!isAtMinDate && selectedDate) {
      const prevDay = moment(selectedDate).subtract(1, 'day').toDate();
      setSelectedDate(prevDay);
    }
  }, [selectedDate, setSelectedDate, isAtMinDate]);

  const handleNextDay = useCallback(() => {
    if (!isAtMaxDate && selectedDate) {
      const nextDay = moment(selectedDate).add(1, 'day').toDate();
      setSelectedDate(nextDay);
    }
  }, [selectedDate, setSelectedDate, isAtMaxDate]);

  const startDateIsToday = !!analyticsStartDate ? moment(analyticsStartDate).isSame(moment(), 'day') : true;

  const updateAnalyticsData = async (selectedDate: any) => {
    const startDate = moment(selectedDate || moment().subtract(1, 'days')).startOf('day');
    const endDate = moment(selectedDate || moment().subtract(1, 'days')).endOf('day');
    const startTimestamp = startDate.unix();
    const endTimestamp = endDate.unix();
    try {
      setFetchingAnalyticsData(true);
      await getHistoricSystemData(startTimestamp, endTimestamp, userSub).then((analyticsData: any) => {
        setSocChartData(processSocChartData(analyticsData));
        const processedSystemData = processSystemEnergyHistoryChartData(analyticsData);
        setSystemEnergyHistoryChartData(processedSystemData);
        calculateYDomain(processedSystemData, setYDomain);
      });
      setFetchingAnalyticsData(false);
    } catch (e: any) {
      if (e?.status === 404) {
        setSocChartData([]);
        setSystemEnergyHistoryChartData([]);
      }
      setFetchingAnalyticsData(false);
      console.error(e);
    }
  };

  useEffect(() => {
    {
      if (startDateIsToday) {
        setDatePickerWarning(
          <>
            <WarningPanel
              title="Not enough data to show your Energy Flow."
              body={`Please wait 24 hours for ${theme.language.appName} to collect your historic data.`}
              linkText={`Why does ${theme.language.appName} not have my data yet?`}
              linkAction={() => setShowNoDataModal(true)}
            />
          </>
        );
      } else {
        setDatePickerWarning(undefined);
      }
    }
  }, [startDateIsToday]);

  const onSelectDate = useCallback((date: Date) => {
    void updateAnalyticsData(date);
    history.push(`/app/energy-flow/${moment(date).unix()}`);
  }, []);

  useOnDateChange(onSelectDate);

  return (
    <>
      {!userDetails?.has_smart_tariff && !isLoadingUserDetails && (
        <SmartTariffWarning
          linkText="Open tariff recommender."
          linkAction={() => {
            history.push(`/app/tariffs`);
          }}
        />
      )}

      {/* @ts-ignore - JS component wrapped with withTheme */}
      <SystemEnergyHistoryChart
        data={systemEnergyHistoryChartData}
        fetchingAnalyticsData={fetchingAnalyticsData}
        yDomain={yDomain}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
        isAtMinDate={isAtMinDate}
        isAtMaxDate={isAtMaxDate}
        selectedDate={selectedDate}
      />
      {/* @ts-ignore - JS component wrapped with withTheme */}
      <BatteryStateOfCharge
        data={socChartData}
        fetchingAnalyticsData={fetchingAnalyticsData}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
        isAtMinDate={isAtMinDate}
        isAtMaxDate={isAtMaxDate}
        selectedDate={selectedDate}
      />
      <InfoModal
        show={showNoDataModal}
        onClose={() => setShowNoDataModal(false)}
        infoContent={
          <div>
            Calculating energy flow can be a complex and time-consuming process. For this reason,{' '}
            {theme.language.appName} performs these calculations once a day to provide a summary from the previous day.{' '}
            <br />
            <br />
            So, when you first sign up, you my need to wait up to 24 hours for energy flow to be available.
          </div>
        }
      />
    </>
  );
};

export default withPageTitle('Energy Flow', EnergyFlowContainer);
